interface TokenCounterProps {
  tokenCount: number;
  limit: number;
  /** 'bar' (default): label + linear bar, used on desktop.
   *  'ring': compact filling circle with no label, used on mobile to save row space. */
  variant?: 'bar' | 'ring';
  className?: string;
}

const getColorClasses = (percentage: number) => {
  if (percentage >= 95) return { bar: 'bg-red-500', text: 'text-red-600', ring: 'stroke-red-500' };
  if (percentage >= 75) return { bar: 'bg-orange-500', text: 'text-orange-600', ring: 'stroke-orange-500' };
  if (percentage >= 60) return { bar: 'bg-yellow-500', text: 'text-yellow-600', ring: 'stroke-yellow-500' };
  return { bar: 'bg-slate-400', text: 'text-slate-500', ring: 'stroke-slate-400' };
};

// Sized to sit flush with the h-8 icon-only Feedback/Export buttons next to it on mobile.
const RING_SIZE = 14;
const RING_STROKE = 2.5;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export const TokenCounter = ({ tokenCount, limit, variant = 'bar', className = '' }: TokenCounterProps) => {
  if (tokenCount <= 0) return null;

  const percentage = Math.min(Math.round((tokenCount / limit) * 100), 100);
  const { bar, text, ring } = getColorClasses(percentage);
  const label = `${percentage}% of context used`;

  if (variant === 'ring') {
    return (
      <div className={`flex items-center gap-1 shrink-0 ${className}`} title={label}>
        <svg
          width={RING_SIZE}
          height={RING_SIZE}
          viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
          className="-rotate-90 shrink-0"
          aria-hidden="true"
        >
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_RADIUS}
            strokeWidth={RING_STROKE}
            fill="none"
            className="stroke-slate-200"
          />
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_RADIUS}
            strokeWidth={RING_STROKE}
            strokeLinecap="round"
            fill="none"
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={RING_CIRCUMFERENCE * (1 - percentage / 100)}
            className={`transition-[stroke-dashoffset] duration-500 ${ring}`}
          />
        </svg>
        {/* The ring alone doesn't read as a precise value at a glance — pairing
            it with the number is what makes "% context used" actually legible. */}
        <span className={`text-[11px] font-medium tabular-nums ${text}`}>{percentage}%</span>
      </div>
    );
  }

  return (
    <div className={`flex flex-col items-end gap-0.5 ${className}`}>
      <span className={`text-xs font-medium ${text}`}>{percentage}% context used</span>
      <div className="w-24 h-1.5 rounded-full bg-slate-200 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${bar}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};
